import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ExternalLinkIcon,
  Link
} from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { Button, Divider } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const READ_MORE_URL =
  'https://support.logmycare.co.uk/en/articles/4851795-how-do-i-add-a-medication'

type AddMedicationFooterProps = {
  isFirstStep: boolean
  isLastStep: boolean
  /** Whether the current step's mandatory fields are filled in. */
  canProceed: boolean
  onBack: () => void
  onCancel: () => void
  onNext: () => void
  /** Without it Save stays disabled; the page can't save until ROST-4088. */
  onSave?: () => void
}

export const AddMedicationFooter = ({
  isFirstStep,
  isLastStep,
  canProceed,
  onBack,
  onCancel,
  onNext,
  onSave
}: AddMedicationFooterProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  return (
    <Footer>
      <Divider />
      <FooterBar>
        <ButtonGroup>
          {!isFirstStep && (
            <Button variant="default" onClick={onBack}>
              <ArrowLeftIcon size={16} />
              {t(emarV2Constants.BACK)}
            </Button>
          )}
          <Link
            href={READ_MORE_URL}
            icon={<ExternalLinkIcon size={16} />}
            iconPosition="right"
            newTabLabel={t(emarV2Constants.OPENS_IN_NEW_TAB)}
          >
            {t(emarV2Constants.READ_MORE)}
          </Link>
        </ButtonGroup>
        <ButtonGroup>
          <Button variant="default" onClick={onCancel}>
            {t(emarV2Constants.CANCEL)}
          </Button>
          {isLastStep ? (
            <Button
              variant="primary"
              onClick={onSave}
              disabled={!canProceed || !onSave}
            >
              {t(emarV2Constants.SAVE)}
            </Button>
          ) : (
            <Button variant="primary" onClick={onNext} disabled={!canProceed}>
              {t(emarV2Constants.NEXT)}
              <ArrowRightIcon size={16} />
            </Button>
          )}
        </ButtonGroup>
      </FooterBar>
    </Footer>
  )
}

const { spacing } = cssVars

/** As designed: 32px either side of the divider. */
const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[400]};
`

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[200]};
`

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[100]};
`
